import React, { useCallback, useMemo, useRef, useState } from "react";

import { Box, Button, Chip, Popover, Stack, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { SelectAutocomplete } from "components/forms";
import dayjs from "dayjs";
import { useDateFormatSelect, useInvestorsSelector } from "store";

import { PickerFilters } from "../hooks/use-picker-filters";

interface PickerFilterBarProps {
  filters: PickerFilters;
  hasActiveFilters: boolean;
  classificationOptions: string[];
  setClassifications: (values: string[]) => void;
  setInvestors: (values: number[]) => void;
  setEffectiveDate: (value: string | null) => void;
  clearFilter: (key: keyof PickerFilters) => void;
  clearAllFilters: () => void;
  children?: React.ReactNode;
}

export const PickerFilterBar: React.FC<PickerFilterBarProps> = ({
  filters,
  hasActiveFilters,
  classificationOptions,
  setClassifications,
  setInvestors,
  setEffectiveDate,
  clearFilter,
  clearAllFilters,
  children
}) => {
  const dateFormat = useDateFormatSelect();
  const investors = useInvestorsSelector();

  const investorMap = useMemo(() => {
    const map = new Map<number, string>();
    investors.forEach((inv) => map.set(inv.id, inv.name));
    return map;
  }, [investors]);

  const [filterEdit, setFilterEdit] = useState<keyof PickerFilters | "">("");
  const [tempClassifications, setTempClassifications] = useState<string[]>([]);
  const [tempInvestors, setTempInvestors] = useState<{ id: number; name: string }[]>([]);
  const [tempDate, setTempDate] = useState<string>("");
  const filterAnchorRef = useRef<HTMLElement | null>(null);

  const investorOptions = useMemo(
    () => investors.map((inv) => ({ id: inv.id, name: inv.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [investors]
  );

  const openFilterEdit = useCallback(
    (e: React.MouseEvent, key: keyof PickerFilters) => {
      filterAnchorRef.current = e.currentTarget as HTMLElement;
      setFilterEdit(key);
      if (key === "classifications") {
        setTempClassifications([...filters.classifications]);
      } else if (key === "investors") {
        setTempInvestors(filters.investors.map((id) => ({ id, name: investorMap.get(id) || `Investor ${id}` })));
      } else if (key === "effectiveDate") {
        setTempDate(filters.effectiveDate || "");
      }
    },
    [filters, investorMap]
  );

  const closeFilterEdit = useCallback(() => {
    setFilterEdit("");
    setTempClassifications([]);
    setTempInvestors([]);
    setTempDate("");
  }, []);

  const saveFilterEdit = useCallback(() => {
    if (filterEdit === "classifications") {
      setClassifications(tempClassifications);
    } else if (filterEdit === "investors") {
      setInvestors(tempInvestors.map((v) => v.id));
    } else if (filterEdit === "effectiveDate") {
      setEffectiveDate(tempDate || null);
    }
    closeFilterEdit();
  }, [
    filterEdit,
    tempClassifications,
    tempInvestors,
    tempDate,
    setClassifications,
    setInvestors,
    setEffectiveDate,
    closeFilterEdit
  ]);

  return (
    <>
      {/* Filter chips */}
      <Stack direction="row" alignItems="center" gap="10px" sx={{ px: "20px", py: "10px" }}>
        <Box sx={{ flex: 1, display: "flex", gap: "10px", alignItems: "center", overflow: "hidden", minWidth: 0 }}>
          <Stack direction="row" gap="8px" sx={{ flexShrink: 0 }}>
            <Chip
              label={`Altius Classification (${filters.classifications.length})`}
              onClick={(e) => openFilterEdit(e, "classifications")}
              onDelete={() => clearFilter("classifications")}
              size="small"
              sx={{ bgcolor: "#f6f8fc" }}
            />
            <Chip
              label={`Investor (${filters.investors.length})`}
              onClick={(e) => openFilterEdit(e, "investors")}
              onDelete={() => clearFilter("investors")}
              size="small"
              sx={{ bgcolor: "#f6f8fc" }}
            />
            <Chip
              label={`Effective Date (${filters.effectiveDate ? 1 : 0})`}
              onClick={(e) => openFilterEdit(e, "effectiveDate")}
              onDelete={() => clearFilter("effectiveDate")}
              size="small"
              sx={{ bgcolor: "#f6f8fc" }}
            />
          </Stack>
          {hasActiveFilters && (
            <Button onClick={clearAllFilters} sx={{ color: "#26a6ae", fontSize: 12, fontWeight: 400 }} variant="text">
              Clear all
            </Button>
          )}
        </Box>
        {children}
      </Stack>

      {/* Filter popover */}
      <Popover
        open={!!filterEdit}
        anchorEl={filterAnchorRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={closeFilterEdit}
        disableEnforceFocus
        disableAutoFocus
        sx={{ zIndex: 1400 }}
        slotProps={{ paper: { sx: { overflow: "visible" } } }}
      >
        <Stack sx={{ alignItems: "stretch", p: "24px", gap: "20px", maxWidth: "350px" }}>
          <Stack
            sx={{ borderBottom: "1px solid #dae0e2", pb: "8px" }}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
              {filterEdit === "classifications" && "Altius Classification"}
              {filterEdit === "investors" && "Investor"}
              {filterEdit === "effectiveDate" && "Effective Date"}
            </Typography>
            <Stack direction="row" gap="4px">
              <Button sx={{ color: "#26A6AE" }} variant="text" onClick={closeFilterEdit}>
                Cancel
              </Button>
              <Button sx={{ color: "#26A6AE" }} variant="text" onClick={saveFilterEdit}>
                Save
              </Button>
            </Stack>
          </Stack>

          {filterEdit === "classifications" && (
            <SelectAutocomplete<string, true>
              slotProps={{ paper: { sx: { zIndex: 1600 } } }}
              multiple
              disableCloseOnSelect
              disablePortal
              selectAll
              limitTags={1}
              openOnFocus
              options={classificationOptions}
              renderInput={(params) => <TextField {...params} label="Classifications" />}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              renderTags={(tagValue, getTagProps) => (
                <div style={{ maxHeight: "160px", overflowY: "auto", overflowX: "hidden" }}>
                  {tagValue.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} />
                  ))}
                </div>
              )}
              sx={{ minWidth: 300 }}
              value={tempClassifications}
              setValue={(value) => setTempClassifications(value)}
            />
          )}

          {filterEdit === "investors" && (
            <SelectAutocomplete<{ id: number; name: string }, true>
              slotProps={{ paper: { sx: { zIndex: 1600 } } }}
              multiple
              disableCloseOnSelect
              disablePortal
              selectAll
              limitTags={1}
              openOnFocus
              options={investorOptions}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              compareParam={(opt) => opt.name}
              renderInput={(params) => <TextField {...params} label="Investors" />}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderTags={(tagValue, getTagProps) => (
                <div style={{ maxHeight: "160px", overflowY: "auto", overflowX: "hidden" }}>
                  {tagValue.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={option.name} />
                  ))}
                </div>
              )}
              sx={{ minWidth: 300 }}
              value={tempInvestors}
              setValue={(value) => setTempInvestors(value)}
            />
          )}

          {filterEdit === "effectiveDate" && (
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={tempDate ? dayjs(tempDate) : null}
              label="Effective Date"
              onChange={(val) => {
                if (val && val.isValid()) setTempDate(val.format("YYYY-MM-DD"));
                else setTempDate("");
              }}
              slotProps={{
                inputAdornment: { position: "start" },
                popper: { sx: { zIndex: 1600, ".MuiPickersCalendarHeader-labelContainer": { fontSize: "0.9rem" } } }
              }}
            />
          )}
        </Stack>
      </Popover>
    </>
  );
};
